import {
  SHContainer,
  SHStack,
  SHTable,
  SHTableContainer,
  SHTableRow,
  SHTypography,
} from "@components/design-systems";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ModelScreenerTableHeader } from "src/pages/managed-accounts/model-screener/components/model-screener-table/table/table-header";
import { ModelScreenerTableBody } from "src/pages/managed-accounts/model-screener/components/model-screener-table/table/table-body";
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { defaultTo } from "lodash";
import { RootState, useAppDispatch } from "@redux/store";
import {
  setModelScreenerDisplayingRowIds,
  setShouldHideToolbar,
  setTableScrolledLeft,
  setTableScrolledTop,
} from "@redux/slices/model-screener";
import { useSelector } from "react-redux";
import { MSTableToolBar } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar";
import { LinearProgress } from "@mui/material";
import {
  ModelScreenerData,
  ModelScreenerRow,
} from "@models/model-screener/entities/modelScreenerData";
import { renderModelScreenerColumnGroups } from "@pages/managed-accounts/model-screener/utils";
import { DisclaimerAssumptionsButton } from "@components/buttons/disclaimer-assumptions";
import { MSTableDisclaimerDialog } from "@pages/managed-accounts/model-screener/components/model-screener-table/disclaimer";
import { useWindowHeight } from "@react-hook/window-size";
import { MSTableDefaultSizeConstants } from "@pages/managed-accounts/model-screener/constant";
import { useUserPermissions } from "@hooks/userUserPermission";

type ModelScreenerTableProps = {
  modelScreenerData?: ModelScreenerData;
};

export const ModelScreenerTable = ({
  modelScreenerData,
}: ModelScreenerTableProps) => {
  const dispatch = useAppDispatch();
  const scrolledTop = useSelector(
    (state: RootState) => state.modelScreener.tableScrollProps.scrolledTop,
  );
  const shouldHideToolbar = useSelector(
    (state: RootState) =>
      state.modelScreener.tableScrollProps.shouldHideToolbar,
  );
  const columnVisibility = useSelector(
    (state: RootState) => state.modelScreener.ui.columnVisibility,
  );
  const isFiltering = useSelector(
    (state: RootState) => state.modelScreener.ui.isFiltering,
  );
  const subHeaderFlattenedIndexes = useSelector(
    (state: RootState) =>
      state.modelScreener.internal.subHeaderFlattenedIndexes,
  );
  const filteredData = useSelector(
    (state: RootState) => state.modelScreener.filteredData,
  );
  const isLoadingDataByDateRange = useSelector(
    (state: RootState) => state.modelScreener.ui.isLoadingDataByDateRange,
  );
  const { isSupplierBasic } = useUserPermissions();

  // Refs and States
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [currentData, setCurrentData] = useState<ModelScreenerRow[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showTableDisclaimer, setShowTableDisclaimer] =
    useState<boolean>(false);
  const isEmptyFilteredData = useMemo(
    () => filteredData.data?.length === 0,
    [filteredData],
  );

  // Table
  const columns = useMemo<ColumnDef<ModelScreenerRow>[]>(
    () =>
      renderModelScreenerColumnGroups({
        data: modelScreenerData,
        subHeaderFlattenedIndexes,
        columnVisibility,
      }),
    [modelScreenerData, subHeaderFlattenedIndexes, columnVisibility],
  );

  const table = useReactTable({
    data: currentData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnVisibility: columnVisibility,
      sorting: sorting,
      columnFilters: columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
  });
  const tableHeaderGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;
  const tableColumns = table.getAllLeafColumns();
  const windowHeight = useWindowHeight();

  // Functions
  const handleOnScroll = () => {
    const currentElement = tableContainerRef.current;

    if (!currentElement) return;

    const scrolledTop = currentElement.scrollTop === 0;
    const scrolledLeft = currentElement.scrollLeft === 0;
    const shouldHideToolbar = scrolledTop
      ? false
      : MSTableDefaultSizeConstants.HeaderGroupHeight +
          (isSupplierBasic
            ? MSTableDefaultSizeConstants.BasicUserMessageHeight
            : 0) +
          defaultTo(filteredData.data?.length, 0) *
            MSTableDefaultSizeConstants.CellHeight >
        windowHeight - MSTableDefaultSizeConstants.TopPaddingToTable;

    dispatch(setTableScrolledTop(scrolledTop));
    dispatch(setTableScrolledLeft(scrolledLeft));
    dispatch(setShouldHideToolbar(shouldHideToolbar));
  };

  // Effects
  useEffect(() => {
    setCurrentData(modelScreenerData?.data ?? []);
    dispatch(setTableScrolledTop(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelScreenerData]);

  useEffect(() => {
    const displayingRow = table
      .getRowModel()
      .flatRows.map((row) => row.original.id);

    dispatch(setModelScreenerDisplayingRowIds(displayingRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, currentData]);

  useEffect(() => {
    setSorting((prev) => prev.filter((sort) => !!columnVisibility[sort.id]));
  }, [columnVisibility]);

  return (
    <SHContainer
      maxWidth={false}
      sx={{
        px: { xs: 0 },
        marginTop: scrolledTop ? 0 : shouldHideToolbar ? "-24px !important" : 0,
        transition: "margin-top 0.5s ease",
      }}
    >
      <SHStack
        spacing={scrolledTop ? 3 : shouldHideToolbar ? 0 : 3}
        width={"100%"}
      >
        <MSTableToolBar />

        <SHTableContainer
          ref={tableContainerRef}
          sx={{
            position: "relative",
            height: "100%",
            maxHeight: scrolledTop
              ? `calc(100vh - 375px)`
              : shouldHideToolbar
              ? `calc(100vh - 285px)`
              : `calc(100vh - 375px)`,
          }}
          onScroll={handleOnScroll}
        >
          <SHTable
            style={{ display: "grid" }}
            sx={{
              td: {
                borderTop: "none",
                borderLeft: "none",
              },
            }}
          >
            <ModelScreenerTableHeader headerGroups={tableHeaderGroups} />
            <SHTableRow
              style={{
                height: "13px",
                border: "none",
                background: "transparent",
              }}
            >
              {(isFiltering || isLoadingDataByDateRange) && (
                <LinearProgress style={{ height: "2px" }} />
              )}
            </SHTableRow>
            <ModelScreenerTableBody
              rows={tableRows}
              columns={tableColumns}
              containerRefElement={tableContainerRef.current}
            />
          </SHTable>
        </SHTableContainer>

        {isEmptyFilteredData && (
          <SHStack width={"100%"} alignItems="center" justifyContent={"center"}>
            <SHTypography variant="body4">
              No results match your criteria.
            </SHTypography>
          </SHStack>
        )}
      </SHStack>

      <SHContainer
        maxWidth={false}
        sx={() => ({
          px: { xs: 0 },
          mt: "10px !important",
        })}
      >
        <SHStack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <SHTypography variant="body4" fontSize={"10px"}>
            By using this table, you confirm that you have read and accepted the{" "}
            <DisclaimerAssumptionsButton
              onClick={() => {
                setShowTableDisclaimer(true);
              }}
            />
          </SHTypography>
        </SHStack>
      </SHContainer>

      {showTableDisclaimer && (
        <MSTableDisclaimerDialog
          onClose={() => setShowTableDisclaimer(false)}
        />
      )}

      {isSupplierBasic && (
        <SHStack marginTop={"2px"}>
          <SHTypography variant="body4" fontSize={"10px"}>
            Subscribe to SuitabilityHub to see models from all managers
          </SHTypography>
        </SHStack>
      )}
    </SHContainer>
  );
};
